import { Component, Input, Output, EventEmitter, Inject, PLATFORM_ID, OnChanges, OnDestroy, SimpleChanges, NgZone, ChangeDetectorRef } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime, Subject, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { User } from '@app/model/user';
import { UserService } from '@app/services';
import { LocalStorageService } from '@app/services';
import { AccountManagerService, SavedAccount } from '../../services/account-manager/account-manager.service';
import { AlertComponent } from '@app/shared/alert/alert.component';
import { ConfirmDialogComponent } from '@app/shared/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-side-bar',
  standalone: true,
  imports: [CommonModule, FormsModule, AlertComponent, ConfirmDialogComponent],
  templateUrl: './side-bar.component.html'
})
export class SideBarComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() fontSize = '18px';
  @Input() isLogin = false;
  @Input() user: User = new User('', '', '', '', '', '', '', 0, 0, 0, '', false, false, false, 0, '', '', '');
  @Input() accountType = '';
  @Input() isCharity = false;
  @Input() isAdmin = false;

  @Output() close = new EventEmitter<void>();
  @Output() fontSizeChange = new EventEmitter<string>();
  @Output() switchAccount = new EventEmitter<void>();

  // Modal states
  logoutModalOpen = false;
  adjustSizeModalOpen = false;
  accountSwitchModalOpen = false;

  // Account switching
  savedAccounts: SavedAccount[] = [];
  isAccountSwitching = false;
  switchingToAccount: SavedAccount | null = null;
  switchPassword = '';
  showPasswordInput = false;
  switchErrorMessage = '';
  staticAlertClosed = true;
  private _switchErrorMessage$ = new Subject<string>();

  // 獲取除了當前登入用戶之外的其他帳號
  get otherAccounts(): SavedAccount[] {
    if (!this.isLogin || !this.user.phone) {
      return this.savedAccounts;
    }
    
    // 標準化電話號碼格式進行比較
    const currentUserPhone = this.normalizePhone(this.user.phone);
    
    return this.savedAccounts.filter(account => {
      const accountPhone = this.normalizePhone(account.phone);
      return accountPhone !== currentUserPhone;
    });
  }

  // 標準化電話號碼格式
  private normalizePhone(phone: string): string {
    if (!phone) return '';
    // 移除所有空格、破折號、括號等
    let normalized = phone.replace(/[\s\-\(\)]/g, '');
    // 將 +886 轉換為統一格式
    if (normalized.startsWith('+886')) {
      normalized = '+886' + normalized.substring(4);
    }
    // 將 0 開頭轉換為 +886
    if (normalized.startsWith('0')) {
      normalized = '+886' + normalized.substring(1);
    }
    return normalized;
  }

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
    private userService: UserService,
    private localStorageService: LocalStorageService,
    private accountManager: AccountManagerService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    // 訂閱保存的帳號列表
    this.accountManager.savedAccounts$.subscribe(accounts => {
      this.savedAccounts = accounts.sort((a, b) => b.lastLoginTime - a.lastLoginTime);
    });
    // 訂閱帳號切換狀態
    this.accountManager.isAccountSwitching$.subscribe(switching => {
      this.isAccountSwitching = switching;
    });

    // 錯誤提示自動關閉
    this._switchErrorMessage$
      .pipe(
        takeUntilDestroyed(),
        tap((msg) => {
          this.switchErrorMessage = msg;
          this.staticAlertClosed = !msg;
        }),
        debounceTime(2000)
      )
      .subscribe(() => {
        this.staticAlertClosed = true;
        this.cdr.markForCheck();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    
    // 當 isOpen 狀態改變時，控制背景滾動
    if (changes['isOpen'] && isPlatformBrowser(this.platformId)) {
      if (changes['isOpen'].currentValue) {
        // 開啟時禁用背景滾動
        this.disableBodyScroll();
      } else {
        // 關閉時恢復背景滾動
        this.enableBodyScroll();
      }
    }
  }

  ngOnDestroy() {
    if (this.isOpen) {
      this.enableBodyScroll();
    }
  }

  onClose() {
    this.close.emit();
  }

  // 鎖定背景捲動，避免 iOS PWA 因固定 body 而重算視窗比例。
  private disableBodyScroll() {
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    }
  }

  private enableBodyScroll() {
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
  }

  // 輔助方法：導航並關閉側邊欄
  private navigateAndClose(route: string[] | string) {
    this.close.emit();
    if (typeof route === 'string') {
      this.router.navigate([route]);
    } else {
      this.router.navigate(route);
    }
  }

  onCreateTask() {
    this.navigateAndClose('create-task-category-page');
  }

  onCreateTaskHistory() {
    this.navigateAndClose('create-task-history');
  }

  onAcceptTaskHistory() {
    this.navigateAndClose('accept-task-history');
  }

  onAdjustSize() {
    this.adjustSizeModalOpen = true;
    // 字體調整不需要關閉側邊欄，因為會打開模態框
  }

  onCoinRecord() {
    this.navigateAndClose('personal-coin-record');
  }

  onCharityRecord() {
    this.navigateAndClose('charity-record');
  }

  onAdminManage() {
    this.navigateAndClose('admin-manage');
  }

  onLogout() {
    this.logoutModalOpen = true;
    // 登出不需要立即關閉側邊欄，因為會打開確認模態框
  }

  parseFontSize(fontSize: string): number {
    return parseFloat(fontSize) || 20;
  }

  // 開啟帳號切換模態框
  onAccountSwitch() {
    this.accountSwitchModalOpen = true;
  }

  // 關閉帳號切換模態框
  closeAccountSwitchModal() {
    this.accountSwitchModalOpen = false;
    this.resetSwitchingState();
  }

  // 重置切換狀態
  private resetSwitchingState() {
    this.showPasswordInput = false;
    this.switchPassword = '';
    this.switchingToAccount = null;
    this.clearError();
  }

  // 開始切換帳號流程
  async startAccountSwitch(account: SavedAccount) {
    this.switchingToAccount = account;
    this.clearError();
    // 嘗試快速切換
    const quickSwitchResult = await this.accountManager.quickSwitchToAccount(account);
    if (quickSwitchResult.success) {
      this.completeAccountSwitch();
    } else if (quickSwitchResult.needPassword) {
      this.showPasswordInput = true;
    } else {
      this.showError(quickSwitchResult.error || '切換失敗');
    }
  }

  // 使用密碼切換帳號
  async switchWithPassword() {
    if (!this.switchingToAccount || !this.switchPassword) {
      return;
    }
    this.clearError();
    const result = await this.accountManager.switchToAccount(
      this.switchingToAccount, 
      this.switchPassword
    );
    if (result.success) {
      this.completeAccountSwitch();
    } else {
      this.showError(result.error || '帳號或密碼錯誤');
    }
  }
  // ===== 共用：錯誤顯示（在 Zone 內 + 標記變更） =====
  private showError(message: string) {
    const zhMessage = this.translateErrorMessage(message);
    this.zone.run(() => {
      this._switchErrorMessage$.next(zhMessage);
      this.staticAlertClosed = false;
      this.cdr.markForCheck();
    });
  }

  /**
   * 將英文錯誤訊息轉為中文
   */
  private translateErrorMessage(message: string): string {
    if (!message) return '';
    const msg = message.toLowerCase();
    if (msg.includes('invalid login credentials') ||
        msg.includes('invalid email or password') ||
        msg.includes('incorrect password')) {
      return '帳號或密碼錯誤，請檢查後重新輸入';
    } else if (msg.includes('too many requests') || msg.includes('rate limit')) {
      return '登入嘗試次數過多，請稍後再試';
    } else if (msg.includes('network') || msg.includes('timeout') || msg.includes('fetch')) {
      return '網路連線異常，請檢查網路狀態後重試';
    } else if (msg.includes('user not found')) {
      return '此帳號不存在，請確認手機號碼或前往註冊';
    } else if (msg.includes('account locked') || msg.includes('locked')) {
      return '帳號已被鎖定，請聯繫客服：0983-408335';
    } else if (msg.includes('email not confirmed') || msg.includes('phone not confirmed')) {
      return '你的帳號尚未驗證或發生錯誤，相關解決辦法請聯繫管理員！';
    } else if (msg.includes('authservice 不可用')) {
      return '系統服務異常，請稍後再試';
    } else if (msg.includes('認證資訊已過期')) {
      return '認證資訊已過期，需要重新輸入密碼';
    }
    return message;
  }

  private clearError() {
    this.zone.run(() => {
      this._switchErrorMessage$.next('');
      this.staticAlertClosed = true;
      this.cdr.markForCheck();
    });
  }

  // 完成帳號切換
  private async completeAccountSwitch() {
    this.accountSwitchModalOpen = false;
    this.resetSwitchingState();
    this.close.emit();

    // 各頁面與 service 都快取了前一個帳號的資料，整頁重新載入到首頁才能完整切換
    if (isPlatformBrowser(this.platformId)) {
      window.location.replace('/home');
      return;
    }

    this.switchAccount.emit();
    await this.router.navigate(['/home']);
  }

  // 取消帳號切換
  cancelAccountSwitch() {
    this.resetSwitchingState();
  }

  // 檢查帳號是否支持快速切換（公開方法）
  canQuickSwitch(account: SavedAccount): boolean {
    return this.accountManager.canQuickSwitch(account);
  }

  // 獲取令牌剩餘時間（公開方法）
  getTokenRemainingTime(account: SavedAccount): number {
    return this.accountManager.getTokenRemainingTime(account);
  }

  // 移除保存的帳號
  removeSavedAccount(event: Event, phone: string) {
    event.stopPropagation();
    this.accountManager.removeAccount(phone);
  }

  // 添加新帳號（導向登入頁面）
  async addNewAccount() {
    
    try {
      this.accountSwitchModalOpen = false;
      this.close.emit();
      
      if (this.isLogin) {
        try {
          await this.userService.signOut();
        } catch (signOutError) {
          console.error(' 登出失敗:', signOutError);
        }
      }
      this.switchAccount.emit();
      this.router.navigate(['/login']).catch((error) => {
        console.error(' 導航失敗:', error);
      });
    } catch (error) {
      console.error(' addNewAccount 執行失敗:', error);
    }
  }

  onLoginPage() {
    this.navigateAndClose('login');
  }

  onRegisterPage() {
    this.navigateAndClose('register');
  }

  // Modal and font size management methods
  async confirmLogout() {
    this.logoutModalOpen = false;
    this.close.emit();

    try {
      this.accountManager.forgetAccountCredential(this.user.phone);
      await this.userService.signOut();

      const switched = await this.switchToNextAvailableAccount();
      if (switched) {
        await this.completeAccountSwitch();
        return;
      }

      this.switchAccount.emit();
      await this.router.navigate(['/login']);
    } catch (error) {
      console.error(' 登出失敗：', error);
      this.switchAccount.emit();
      await this.router.navigate(['/login']);
    }
  }

  private async switchToNextAvailableAccount(): Promise<boolean> {
    const candidates = this.otherAccounts
      .filter(account => this.accountManager.canQuickSwitch(account))
      .sort((a, b) => b.lastLoginTime - a.lastLoginTime);

    for (const account of candidates) {
      const result = await this.accountManager.quickSwitchToAccount(account);
      if (result.success) {
        return true;
      }
    }

    return false;
  }

  cancelLogout() {
    this.logoutModalOpen = false;
  }

  closeAdjustSizeModal() {
    this.adjustSizeModalOpen = false;
    this.close.emit(); // 同時關閉側邊欄
  }

  confirmFontSize() {
    if (isPlatformBrowser(this.platformId)) {
      window.location.reload();
    }
  }

  changeFontSize(size: string) {
    if (isPlatformBrowser(this.platformId)) {
      this.localStorageService.setItem('fontSize', this.parseFontSize(size));
      this.fontSizeChange.emit(size);
    }
    // 先套用預覽；按下「確定」後再重新載入目前頁面。
  }

  /**
   *  手動延長快速切換有效時間
   */
  refreshAccountToken(account: SavedAccount, event: Event) {
    event.stopPropagation(); // 防止觸發帳號切換
    
    try {
      this.accountManager.refreshTokenForAccount(account.phone);
    } catch (error) {
      console.error(' 延長快速切換有效時間時發生錯誤:', error);
    }
  }
}
